import ReportingHeader from '../components/ReportingHeader';
import { useReporting } from '../components/ReportingProvider';
import { TyreReportCharts } from '../components/TyreReportCharts';
import EmptyState from '../components/EmptyState';
import { StatCardsSkeleton } from '../components/TmSkeletonLayouts';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { formatCurrency, formatNumber } from '../lib/utils';

const distance = (value) => value == null ? 'Unknown' : `${formatNumber(value, { maximumFractionDigits: 0 })} km`;
const count = (value) => value == null ? 'Unknown' : formatNumber(value);
const Metric = ({ label, value, note }) => <Card><CardContent className="p-4 sm:p-5">
  <p className="text-xs font-semibold text-slate-600">{label}</p>
  <p className="my-2 text-2xl font-extrabold tabular-nums text-slate-950">{value}</p>
  <p className="text-xs leading-5 text-slate-500">{note}</p>
</CardContent></Card>;
const SectionHeading = ({ id, scope, title, description }) => <div className="mb-4 border-l-4 border-operational-600 pl-4">
  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-operational-700">{scope}</p>
  <h2 id={id} className="text-xl font-bold text-slate-950">{title}</h2>
  <p className="mt-1 text-sm text-slate-500">{description}</p>
</div>;

export default function TyreAnalyticsPage() {
  const { workspaces: { analytics: workspace }, transfer } = useReporting();
  const { report, reportState, viewedReport, activity, summary, period } = workspace;
  const money = (value) => value == null ? 'Unknown' : formatCurrency(value, report.currency);
  return <div className="space-y-5">
    <ReportingHeader view="analytics" />
    {!viewedReport && reportState.error && <EmptyState title="Analytics unavailable" description={reportState.error} action={<Button onClick={reportState.reload}>Retry</Button>} />}
    {!viewedReport && reportState.loading && <div role="status"><p className="mb-3 text-sm text-slate-500">Loading analytics…</p><StatCardsSkeleton count={4} columns="sm:grid-cols-2 xl:grid-cols-4" /></div>}
    {report && <div className="space-y-8">
      <section aria-labelledby="activity-title">
        <SectionHeading id="activity-title" scope={period} title="Period activity" description="Installations and operations recorded in the selected period." />
        <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Tyre installations" value={count(activity.installations)} note={`${count(activity.newInstallations)} first life · ${count(activity.retreadedInstallations)} retreaded · ${count(activity.unknownCategoryInstallations)} unknown`} />
          <Metric label="Replacements" value={count(activity.replacements)} note="Replacement operations" />
          <Metric label="Transfers / rotations" value={count(activity.transfers)} note={`${count(activity.removals)} removals recorded`} />
          <Metric label="Completed fitment km" value={distance(activity.completedFitmentDistanceKm)} note={`${count(activity.unknownDistanceCount)} fitments with unknown distance. Full completed runs, not period mileage.`} />
        </div>
        <TyreReportCharts report={report} onVehicleSelect={(id) => transfer('analytics', 'reports', id)} />
      </section>
      <section aria-labelledby="fleet-title">
        <SectionHeading id="fleet-title" scope={viewedReport?.job ? 'At generation time' : 'Current records'} title="Fleet profile" description="Deployment and condition of matching tyres. Date filters do not turn these figures into a historical fleet snapshot." />
        <TyreReportCharts report={report} group="fleet" />
      </section>
      <section aria-labelledby="economics-title">
        <SectionHeading id="economics-title" scope={viewedReport?.job ? 'Lifetime · stored snapshot' : 'Lifetime · matching tyres'} title="Lifetime economics" description="Cumulative distance and costs, including credits and disposal proceeds. Unknown distances are excluded." />
        <div className="grid gap-4 md:grid-cols-3">
          <Metric label="Lifetime distance" value={distance(summary.lifetimeDistanceKm)} note={`${count(summary.unknownDistanceCount)} tyres with unknown distance`} />
          <Metric label="Lifetime cost" value={money(summary.lifetimeCost)} note="Across the matching tyre population" />
          <Metric label="Lifetime cost / km" value={money(summary.fleetCostPerKm)} note="Not a cost rate for the selected period" />
        </div>
      </section>
    </div>}
  </div>;
}
